import { ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { trackConversion } from "@/utils/analytics";
import { trackClick } from "@/utils/tracker";

const WhatsAppIcon = () =>
<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>;


const TelegramIcon = () =>
<svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
    <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
  </svg>;


export const HeroSection = ({ city, onOrderClick, onWhatsAppClick, lang = "ru", t, titleOverride, subtitleOverride }) => {
  const heroImage = city?.heroImage || "/hero.webp";
  const openTelegram = () => {
    trackConversion("telegram");
    trackClick("telegram", city?.slug);
    window.open("https://t.me/Yucca_flower", "_blank");
  };

  const openOtherCity = () => {
    trackConversion("whatsapp", "other-city");
    trackClick("whatsapp", city?.slug ? `${city.slug}/other-city` : "other-city");
    const msg = t ? t("wa.otherCity") : "Здравствуйте! Моего города нет на сайте — можно ли заказать доставку цветов в другой город?";
    window.open(`https://wa.me/905316224378?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const cityPrep = lang === "tr" ? (city?.namePrepositionalTr || city?.namePrepositionalEn) : (lang === "en" ? city?.namePrepositionalEn : city?.namePrepositional);
  const altText = t ? t("alt.hero", { city: cityPrep }) : `Flower delivery ${cityPrep}`;

  return (
    <section
      className="relative min-h-[90vh] sm:min-h-screen flex items-center pt-16 sm:pt-20 overflow-hidden"
      data-testid="hero-section">

      {/* Background Image */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src={heroImage}
          alt={altText}
          className="w-full h-full object-cover"
          width={1920}
          height={1080}
          style={{ objectPosition: 'center center', minHeight: '100%', minWidth: '100%' }}
          loading="eager"
          fetchPriority="high" />

        <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/55 to-black/20"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80 sm:hidden"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full">
        <div className="max-w-xl lg:max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-pink-500 to-rose-500 px-4 py-2 rounded-full mb-6 animate-fade-in shadow-lg">
            <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span>
            <span className="text-sm font-semibold text-white">{t ? t("hero.badge") : "Доставка сегодня"}</span>
          </div>

          {/* Heading */}
          <h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6 animate-fade-in-up drop-shadow-lg text-balance"
            style={{ fontFamily: 'Playfair Display, serif' }}>

            {t ? t("hero.title") : "Доставка цветов"} <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#FF69B4] to-[#FF003F]">{titleOverride || cityPrep || "in Antalya"}</span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-white/90 mb-8 max-w-md animate-fade-in-up stagger-1 drop-shadow-md font-medium">
            {subtitleOverride || (t ? t("hero.subtitle", { city: cityPrep }) : "")}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-row gap-3 mt-2 sm:mt-0 animate-fade-in-up stagger-2">
            <Button
              onClick={openTelegram}
              className="bg-white/15 backdrop-blur-md hover:bg-white/25 text-[#0088cc] text-sm sm:text-base h-11 sm:h-14 px-5 sm:px-8 rounded-full font-semibold transition-all hover:scale-105 border-2 border-[#0088cc]/40 hover:border-[#0088cc]/70 shadow-[0_0_15px_rgba(0,136,204,0.15)]"
              data-testid="hero-telegram-btn">

              <TelegramIcon />
              <span className="ml-2">Telegram</span>
            </Button>
            
            <Button
              onClick={onWhatsAppClick}
              className="bg-white/15 backdrop-blur-md hover:bg-white/25 text-[#25d366] text-sm sm:text-base h-11 sm:h-14 px-5 sm:px-8 rounded-full font-semibold transition-all hover:scale-105 border-2 border-[#25d366]/40 hover:border-[#25d366]/70 shadow-[0_0_15px_rgba(37,211,102,0.15)]"
              data-testid="hero-whatsapp-btn">

              <WhatsAppIcon />
              <span className="ml-2">WhatsApp</span>
            </Button>
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center gap-3 sm:gap-6 mt-8 sm:mt-12 animate-fade-in-up stagger-3">
            <button
              type="button"
              onClick={openOtherCity}
              className="group w-full sm:w-auto order-first sm:order-last flex items-center justify-center sm:justify-start gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 hover:border-pink-300/60 transition-colors text-left"
              data-testid="hero-other-city-btn">
              <MapPin className="w-4 h-4 text-pink-300 shrink-0" />
              <span className="text-sm text-white font-semibold">
                {t ? t("hero.otherCity") : "Нет вашего города?"}{" "}
                <span className="text-pink-300 whitespace-nowrap group-hover:underline underline-offset-2">
                  {t ? t("hero.otherCityAction") : "Доставим под заказ"}
                  <ArrowRight className="inline w-3.5 h-3.5 ml-1 transition-transform group-hover:translate-x-0.5" />
                </span>
              </span>
            </button>
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <div className="flex -space-x-1">
                <span className="text-yellow-400 text-lg">&#9733;</span>
                <span className="text-yellow-400 text-lg">&#9733;</span>
                <span className="text-yellow-400 text-lg">&#9733;</span>
                <span className="text-yellow-400 text-lg">&#9733;</span>
                <span className="text-yellow-400 text-lg">&#9733;</span>
              </div>
              <span className="text-sm text-white font-semibold">{t ? t("hero.orders") : "10,000+ заказов"}</span>
            </div>
            <div className="bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
              <span className="text-sm text-white font-semibold">
                <span className="text-yellow-400">24/7</span> {t ? t("hero.online") : "Онлайн"}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce hidden sm:block">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse"></div>
        </div>
      </div>
    </section>);

};

export default HeroSection;
